import * as types from './actionTypes';

import { AxiosRequestConfig } from 'axios';
import { omit, orderBy } from 'lodash';
import { IjobPart, Ioption, Ipart, ThunkResult } from '../models';

import { msalFetch } from '../components/auth/Auth-Utils';
import API from '../constants/apiEndpoints';
import { constants } from '../constants/constants';
import { initialPart } from '../reducers/initialState';
import { beginAjaxCall } from './ajaxStatusActions';

/*
 * this is used for paging and filtering the jobs on the manage Jobs view
 * NOTE: the server automatically filters by the user's country
 */

/**
 * Backend Part Search returns Material (id, number, description). Normalize to Ipart shape for options.
 */
const materialToPart = (item: any): Ipart => ({
    ...initialPart,
    id: item.id ?? item.Id ?? '',
    number: item.number ?? item.Number ?? '',
    description: item.description ?? item.Description ?? item.name ?? '',
    installationTime: item.installationTime ?? item.InstallationTime ?? 0
});

/**
 * @param parts array of part or Material objects
 * @returns partOptions sorted by the part description
 */
export const partsToOptions = (parts: unknown): Ioption[] => {
    if (Array.isArray(parts)) {
        const partOptions = parts.map((item: any) => {
            const part = materialToPart(item);
            return {
                value: part.id,
                label: `${part.number} - ${part.description}`,
                order: part.description
            };
        });
        return orderBy(partOptions, 'order');
    } else {
        return [];
    }
};

export const searchPartsForAsyncSelect = (
    search: string
): Promise<Ioption[]> => {
    const url = API.part.search;
    const axiosOptions: AxiosRequestConfig = {
        method: 'get',
        params: { number: search }
    };
    return msalFetch(url, axiosOptions)
        .then(data => {
            if (!data.data) {
                throw new Error('missing data');
            } else {
                return partsToOptions(data.data.result);
            }
        })
        .catch((error: any) => {
            constants.handleError(error, 'searchPartsForAsyncSelect');
            console.error(error);
            throw new Error(error);
        });
};

export const getJobParts = (): ThunkResult<any> => {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        const url = API.part.get;
        const jobID = getState().manageJob.selectedJob.id;
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: { jobID }
        };
        return msalFetch(url, axiosOptions)
            .then(data => {
                if (!data.data) {
                    throw new Error('missing data');
                } else {
                    dispatch({
                        type: types.GET_JOB_PARTS_SUCCESS,
                        payload: data.data
                    });
                    return data;
                }
            })
            .catch((error: any) => {
                dispatch({
                    type: types.GET_JOB_PARTS_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'getJobParts');
                console.error(error);
            });
    };
};

/*
 * saveJob
 * persist the job to the Server.
 * Backend expects JobPart with materialID (not partID). Send materialID from partID.
 */
export function saveJobParts(jobParts: IjobPart[]): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());
        const url = API.part.save;
        const payload = jobParts.map(jp => ({
            ...omit(jp, 'partID', 'part'),
            materialID: jp.partID
        }));
        const axiosOptions: AxiosRequestConfig = {
            method: 'post',
            data: payload
        };

        return msalFetch(url, axiosOptions)
            .then(data => {
                if (!data.data) {
                    throw new Error('missing data');
                }
                dispatch({
                    type: types.SAVE_JOB_PARTS_SUCCESS,
                    payload: jobParts
                });
                return data;
            })
            .catch((error: any) => {
                dispatch({
                    type: types.SAVE_JOB_PARTS_FAILED,
                    error,
                    axiosOptions
                });
                constants.handleError(error, 'save job parts');
                console.error(error);
            });
    };
}
